/**
 * Mobile Gallery Lightbox
 * -----------------------------------------------------------------------------
 * Abre um carrossel fullscreen (fundo #000 @ 0.8) ao tocar em uma foto da
 * galeria de produto no mobile, com pinch-zoom, duplo toque, swipe lateral
 * entre as fotos e arrastar para baixo para fechar.
 *
 * Não depende de nenhuma biblioteca externa.
 */
(function () {
  'use strict';

  const CONFIG = {
    enableOn: 'all', // 'all' | 'mobile' | 'desktop'
    mobileQuery: '(max-width: 749px)',
    itemSelector: '.product-gallery__media, .mlb-zoom-target, .zoomable',
    minScale: 1,
    maxScale: 4,
    doubleTapScale: 2.5,
    clickZoomScale: 2.5, // zoom aplicado no clique (desktop)
    wheelZoom: true, // roda do mouse / pinça do trackpad
    disableThemeZoom: true, // remove a classe .zoomable (zoom de hover do tema)
    swipeRatio: 0.18, // % da largura da tela para trocar de slide
    closeDragDistance: 110, // px de arrasto vertical para fechar (só toque)
    hintDuration: 3200, // ms que a dica fica visível
    hintOncePerSession: false, // true = mostra a dica só uma vez por sessão
    hintOnDesktop: true, // false = dica só no mobile
    hintTextTouch: 'Use dois dedos para ampliar',
    hintTextMouse: 'Clique para ampliar',
    hintIconUrl: null // ex.: window.MLB_HINT_ICON para usar um SVG/PNG do tema
  };

  const HINT_ICON_TOUCH = `
    <svg class="mlb__hint-icon mlb__hint-icon--touch" viewBox="0 0 64 64" aria-hidden="true">
      <g class="mlb__hint-arrow mlb__hint-arrow--left">
        <path d="M28 32H8" />
        <path d="M15 25l-7 7 7 7" />
      </g>
      <g class="mlb__hint-arrow mlb__hint-arrow--right">
        <path d="M36 32h20" />
        <path d="M49 25l7 7-7 7" />
      </g>
    </svg>`;

  const HINT_ICON_MOUSE = `
    <svg class="mlb__hint-icon mlb__hint-icon--mouse" viewBox="0 0 64 64" aria-hidden="true">
      <circle cx="28" cy="28" r="16" />
      <path d="M39.5 39.5 52 52" />
      <path d="M28 21v14 M21 28h14" />
    </svg>`;

  const clamp = (v, min, max) => Math.min(Math.max(v, min), max);
  const isMobile = () => window.matchMedia(CONFIG.mobileQuery).matches;
  const isTouch = () => window.matchMedia('(pointer: coarse)').matches;
  const distance = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
  const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  function isEnabled() {
    if (CONFIG.enableOn === 'mobile') return isMobile();
    if (CONFIG.enableOn === 'desktop') return !isMobile();
    return true;
  }

  let refs = null;
  let slides = [];
  let sources = [];
  let index = 0;
  let isOpen = false;
  let gesture = null;
  let hintShown = false;
  let hintTimer = null;
  let lastTapAt = 0;
  let scrollY = 0;
  const pointers = new Map();

  /* ---------------------------------------------------------------- DOM -- */

  function build() {
    if (refs) return refs;

    const root = document.createElement('div');
    root.className = 'mlb';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Galeria de imagens do produto');

    const iconMarkup = CONFIG.hintIconUrl
      ? `<img class="mlb__hint-custom" src="${CONFIG.hintIconUrl}" alt="" aria-hidden="true">`
      : HINT_ICON_TOUCH + HINT_ICON_MOUSE;

    root.innerHTML = `
      <div class="mlb__backdrop" data-mlb-close></div>
      <div class="mlb__track"></div>
      <button class="mlb__close" type="button" aria-label="Fechar galeria" data-mlb-close>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5 19 19M19 5 5 19"/></svg>
      </button>
      <button class="mlb__nav mlb__nav--prev" type="button" aria-label="Foto anterior" data-mlb-prev>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5 8 12l7 7"/></svg>
      </button>
      <button class="mlb__nav mlb__nav--next" type="button" aria-label="Próxima foto" data-mlb-next>
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>
      </button>
      <p class="mlb__counter"></p>
      <div class="mlb__hint" hidden>
        ${iconMarkup}
        <span class="mlb__hint-text"></span>
      </div>
    `;

    document.body.appendChild(root);

    refs = {
      root: root,
      track: root.querySelector('.mlb__track'),
      counter: root.querySelector('.mlb__counter'),
      hint: root.querySelector('.mlb__hint'),
      hintText: root.querySelector('.mlb__hint-text'),
      prev: root.querySelector('[data-mlb-prev]'),
      next: root.querySelector('[data-mlb-next]')
    };

    root.addEventListener('click', function (event) {
      if (event.target.closest('[data-mlb-close]')) return close();
      if (event.target.closest('[data-mlb-prev]')) return goTo(index - 1);
      if (event.target.closest('[data-mlb-next]')) return goTo(index + 1);
    });

    refs.track.addEventListener('pointerdown', onPointerDown);
    refs.track.addEventListener('pointermove', onPointerMove);
    refs.track.addEventListener('pointerup', onPointerUp);
    refs.track.addEventListener('pointercancel', onPointerUp);
    refs.track.addEventListener('wheel', onWheel, { passive: false });
    refs.track.addEventListener('dragstart', (e) => e.preventDefault());
    refs.track.addEventListener('contextmenu', (e) => e.preventDefault());

    document.addEventListener('keydown', function (event) {
      if (!isOpen) return;
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') goTo(index - 1);
      if (event.key === 'ArrowRight') goTo(index + 1);
    });

    window.addEventListener('resize', function () {
      if (isOpen && !isEnabled()) close();
    });

    return refs;
  }

  /* ------------------------------------------------------------ coletar -- */

  function collect(container, clicked) {
    const nodes = Array.prototype.slice.call(
      container.querySelectorAll(CONFIG.itemSelector)
    );

    if (nodes.indexOf(clicked) === -1) nodes.push(clicked);

    return nodes
      .filter(function (node) {
        if (node.hasAttribute('hidden')) return false; // respeita o filtro por variante
        if (node.dataset.mediaType && node.dataset.mediaType !== 'image') return false;
        return true;
      })
      .map(function (node) {
        const img = node.querySelector('img');
        const src =
          node.dataset.zoomSrc ||
          (img && (img.currentSrc || img.src)) ||
          null;

        return {
          node: node,
          src: src,
          width: parseInt(node.dataset.zoomWidth, 10) || null,
          height: parseInt(node.dataset.zoomHeight, 10) || null,
          alt: (img && img.alt) || ''
        };
      })
      .filter((item) => Boolean(item.src));
  }

  /* --------------------------------------------------------- abrir/fechar */

  function open(list, startIndex) {
    build();

    sources = list;
    refs.track.innerHTML = '';
    slides = list.map(function (item, i) {
      const slide = document.createElement('div');
      slide.className = 'mlb__slide';

      const img = document.createElement('img');
      img.className = 'mlb__img';
      img.src = item.src;
      img.alt = item.alt;
      img.decoding = 'async';
      img.draggable = false;
      if (i !== startIndex) img.loading = 'lazy';
      if (item.width && item.height) {
        img.width = item.width;
        img.height = item.height;
      }

      slide.appendChild(img);
      refs.track.appendChild(slide);

      return { el: slide, img: img, scale: 1, x: 0, y: 0 };
    });

    isOpen = true;
    hintShown = false;
    lockScroll();

    const touch = isTouch();
    refs.root.classList.toggle('mlb--touch', touch);
    refs.root.classList.toggle('mlb--mouse', !touch);
    refs.root.classList.remove('is-zoomed');
    refs.hintText.textContent = touch ? CONFIG.hintTextTouch : CONFIG.hintTextMouse;

    refs.root.hidden = false;
    refs.root.style.setProperty('--mlb-backdrop-opacity', '0.8');
    requestAnimationFrame(() => refs.root.classList.add('is-open'));

    goTo(startIndex, false);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    gesture = null;
    pointers.clear();
    clearTimeout(hintTimer);

    refs.root.classList.remove('is-open');
    unlockScroll();

    // devolve a galeria da página na mesma foto que o usuário estava vendo
    const current = sources[index];
    if (current && current.node && current.node.scrollIntoView) {
      current.node.scrollIntoView({ block: 'nearest', inline: 'center' });
    }

    setTimeout(function () {
      if (isOpen) return;
      refs.root.hidden = true;
      refs.track.innerHTML = '';
      slides = [];
      sources = [];
    }, 260);
  }

  function goTo(target, animate) {
    if (!slides.length) return;
    index = clamp(target, 0, slides.length - 1);

    slides.forEach(function (slide, i) {
      if (i !== index) resetSlide(slide, false);
    });

    refs.track.style.transition =
      animate === false ? 'none' : 'transform 0.3s cubic-bezier(0.2, 0.7, 0.3, 1)';
    refs.track.style.transform = 'translate3d(' + -index * 100 + '%, 0, 0)';
    refs.counter.textContent = index + 1 + ' / ' + slides.length;

    updateNav();
    maybeShowHint();
  }

  /**
   * A primeira foto não tem "anterior" e a última não tem "próximo": nesses
   * casos a seta some. Com uma foto só, nenhuma das duas aparece. E enquanto a
   * foto está ampliada as setas saem da frente.
   */
  function updateNav() {
    if (!refs) return;
    const single = slides.length <= 1;
    refs.prev.disabled = single || index === 0;
    refs.next.disabled = single || index === slides.length - 1;
    refs.root.classList.toggle(
      'is-zoomed',
      Boolean(slides[index]) && slides[index].scale > 1.02
    );
  }

  /* -------------------------------------------------------------- gestos */

  function onPointerDown(event) {
    if (!isOpen || !slides.length) return;
    refs.track.setPointerCapture && refs.track.setPointerCapture(event.pointerId);
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    const slide = slides[index];
    refs.track.style.transition = 'none';

    if (pointers.size === 2) {
      hideHint();
      const points = Array.from(pointers.values());
      const rect = slide.img.getBoundingClientRect();
      const mid = midpoint(points[0], points[1]);

      // centro "sem transform": o scale não move o centro, só o translate move
      const cx = rect.left + rect.width / 2 - slide.x;
      const cy = rect.top + rect.height / 2 - slide.y;

      gesture = {
        type: 'pinch',
        startDistance: distance(points[0], points[1]) || 1,
        startScale: slide.scale,
        cx: cx,
        cy: cy,
        baseW: rect.width / slide.scale,
        baseH: rect.height / slide.scale,
        localX: (mid.x - cx - slide.x) / slide.scale,
        localY: (mid.y - cy - slide.y) / slide.scale
      };
      return;
    }

    if (pointers.size === 1) {
      const zoomed = slide.scale > 1.02;
      const rect = slide.img.getBoundingClientRect();

      gesture = {
        type: zoomed ? 'pan' : 'swipe',
        pointerType: event.pointerType || 'mouse',
        startX: event.clientX,
        startY: event.clientY,
        originX: slide.x,
        originY: slide.y,
        baseW: rect.width / slide.scale,
        baseH: rect.height / slide.scale,
        startedAt: Date.now(),
        axis: null,
        moved: false
      };
    }
  }

  function onPointerMove(event) {
    if (!gesture || !pointers.has(event.pointerId)) return;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    const slide = slides[index];

    /* pinça ------------------------------------------------------------- */
    if (gesture.type === 'pinch') {
      if (pointers.size < 2) return;
      const points = Array.from(pointers.values());
      const mid = midpoint(points[0], points[1]);
      const ratio = distance(points[0], points[1]) / gesture.startDistance;
      const scale = clamp(gesture.startScale * ratio, CONFIG.minScale, CONFIG.maxScale);

      slide.scale = scale;
      slide.x = mid.x - gesture.cx - gesture.localX * scale;
      slide.y = mid.y - gesture.cy - gesture.localY * scale;
      clampPan(slide, gesture.baseW, gesture.baseH);
      applySlide(slide, false);
      return;
    }

    const dx = event.clientX - gesture.startX;
    const dy = event.clientY - gesture.startY;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
      gesture.moved = true;
      hideHint();
    }

    /* arrastar a foto ampliada ------------------------------------------ */
    if (gesture.type === 'pan') {
      slide.x = gesture.originX + dx;
      slide.y = gesture.originY + dy;
      clampPan(slide, gesture.baseW, gesture.baseH);
      applySlide(slide, false);
      return;
    }

    /* swipe entre fotos / arrastar para fechar --------------------------- */
    if (!gesture.axis && gesture.moved) {
      // com mouse, arrastar para baixo não fecha: só existe eixo horizontal
      const horizontal =
        gesture.pointerType === 'mouse' || Math.abs(dx) > Math.abs(dy);
      gesture.axis = horizontal ? 'x' : 'y';
    }

    if (gesture.axis === 'x') {
      const atEdge =
        (index === 0 && dx > 0) || (index === slides.length - 1 && dx < 0);
      const offset = dx * (atEdge ? 0.35 : 1);
      refs.track.style.transform =
        'translate3d(calc(' + -index * 100 + '% + ' + offset + 'px), 0, 0)';
    } else if (gesture.axis === 'y') {
      const progress = clamp(Math.abs(dy) / 320, 0, 1);
      refs.track.style.transform =
        'translate3d(' + -index * 100 + '%, ' + dy + 'px, 0)';
      refs.root.style.setProperty(
        '--mlb-backdrop-opacity',
        (0.8 * (1 - progress * 0.7)).toFixed(2)
      );
    }
  }

  function onPointerUp(event) {
    if (!gesture) {
      pointers.delete(event.pointerId);
      return;
    }

    const slide = slides[index];
    const wasPinch = gesture.type === 'pinch';
    pointers.delete(event.pointerId);

    if (wasPinch) {
      if (pointers.size >= 2) return;
      if (slide.scale <= 1.05) resetSlide(slide, true);
      gesture = null;
      return;
    }

    if (gesture.type === 'pan') {
      // clicou sem arrastar? é um toque, não um arrasto: desfaz o zoom.
      // Se houve arrasto (gesture.moved), não faz nada — o usuário estava
      // movendo a foto, e desfazer o zoom aqui seria frustrante.
      if (!gesture.moved) handleTap(event);
      gesture = null;
      return;
    }

    const dx = event.clientX - gesture.startX;
    const dy = event.clientY - gesture.startY;
    const elapsed = Date.now() - gesture.startedAt;
    const width = refs.track.clientWidth || window.innerWidth;

    if (!gesture.moved) {
      handleTap(event);
      gesture = null;
      return;
    }

    if (gesture.axis === 'y') {
      const shouldClose =
        Math.abs(dy) > CONFIG.closeDragDistance ||
        (Math.abs(dy) > 50 && elapsed < 250);
      refs.root.style.setProperty('--mlb-backdrop-opacity', '0.8');
      if (shouldClose) {
        gesture = null;
        close();
        return;
      }
      goTo(index);
      gesture = null;
      return;
    }

    const shouldSwipe =
      Math.abs(dx) > width * CONFIG.swipeRatio ||
      (Math.abs(dx) > 40 && elapsed < 250);

    goTo(shouldSwipe ? index + (dx < 0 ? 1 : -1) : index);
    gesture = null;
  }

  function handleTap(event) {
    const slide = slides[index];
    const zoomed = slide.scale > 1.02;
    const rect = slide.img.getBoundingClientRect();
    const outside =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;

    // com a foto ampliada, clicar fora desfaz o zoom em vez de fechar:
    // fechar sem querer no meio de uma inspeção é o pior desfecho possível
    if (zoomed && outside) {
      resetSlide(slide, true);
      return;
    }

    // sem zoom, tocar/clicar na área vazia ao redor da foto fecha
    if (outside) {
      close();
      return;
    }

    // no mouse, um clique já amplia; no toque, mantém o duplo toque
    if (event.pointerType === 'mouse') {
      toggleZoom(event.clientX, event.clientY, CONFIG.clickZoomScale);
      return;
    }

    const now = Date.now();
    if (now - lastTapAt < 300) {
      lastTapAt = 0;
      toggleZoom(event.clientX, event.clientY, CONFIG.doubleTapScale);
    } else {
      lastTapAt = now;
    }
  }

  function toggleZoom(px, py, targetScale) {
    const slide = slides[index];
    const rect = slide.img.getBoundingClientRect();
    const cx = rect.left + rect.width / 2 - slide.x;
    const cy = rect.top + rect.height / 2 - slide.y;
    const baseW = rect.width / slide.scale;
    const baseH = rect.height / slide.scale;

    if (slide.scale > 1.05) {
      resetSlide(slide, true);
      return;
    }

    const target = clamp(
      targetScale || CONFIG.doubleTapScale,
      CONFIG.minScale,
      CONFIG.maxScale
    );
    const localX = (px - cx - slide.x) / slide.scale;
    const localY = (py - cy - slide.y) / slide.scale;

    slide.scale = target;
    slide.x = px - cx - localX * target;
    slide.y = py - cy - localY * target;
    clampPan(slide, baseW, baseH);
    applySlide(slide, true);
  }

  /**
   * Zoom pela roda do mouse e pela pinça do trackpad (que o navegador entrega
   * como wheel + ctrlKey). Ancorado no cursor, igual à pinça no toque.
   */
  function onWheel(event) {
    if (!isOpen || !CONFIG.wheelZoom || !slides.length) return;
    event.preventDefault();

    const slide = slides[index];
    const rect = slide.img.getBoundingClientRect();
    const cx = rect.left + rect.width / 2 - slide.x;
    const cy = rect.top + rect.height / 2 - slide.y;
    const baseW = rect.width / slide.scale;
    const baseH = rect.height / slide.scale;

    const intensity = event.ctrlKey ? 0.012 : 0.0022;
    const next = clamp(
      slide.scale * Math.exp(-event.deltaY * intensity),
      CONFIG.minScale,
      CONFIG.maxScale
    );

    const localX = (event.clientX - cx - slide.x) / slide.scale;
    const localY = (event.clientY - cy - slide.y) / slide.scale;

    slide.scale = next;
    slide.x = event.clientX - cx - localX * next;
    slide.y = event.clientY - cy - localY * next;
    clampPan(slide, baseW, baseH);
    applySlide(slide, false);
    hideHint();
  }

  /* ----------------------------------------------------------- helpers -- */

  function clampPan(slide, baseW, baseH) {
    const maxX = Math.max(0, (baseW * slide.scale - window.innerWidth) / 2);
    const maxY = Math.max(0, (baseH * slide.scale - window.innerHeight) / 2);
    slide.x = clamp(slide.x, -maxX, maxX);
    slide.y = clamp(slide.y, -maxY, maxY);
  }

  function applySlide(slide, animate) {
    slide.img.style.transition = animate ? 'transform 0.25s ease' : 'none';
    slide.img.style.transform =
      'translate3d(' + slide.x + 'px, ' + slide.y + 'px, 0) scale(' + slide.scale + ')';
    const zoomed = slide.scale > 1.02;
    slide.el.classList.toggle('is-zoomed', zoomed);
    if (refs && slides[index] === slide) {
      refs.root.classList.toggle('is-zoomed', zoomed);
    }
  }

  function resetSlide(slide, animate) {
    slide.scale = 1;
    slide.x = 0;
    slide.y = 0;
    applySlide(slide, animate);
  }

  function maybeShowHint() {
    if (!refs || hintShown || index !== 0) return;
    if (!isTouch() && !CONFIG.hintOnDesktop) return;
    if (CONFIG.hintOncePerSession) {
      try {
        if (sessionStorage.getItem('mlb-hint-seen')) return;
      } catch (e) {}
    }

    hintShown = true;
    refs.hint.hidden = false;
    requestAnimationFrame(() => refs.hint.classList.add('is-visible'));

    clearTimeout(hintTimer);
    hintTimer = setTimeout(hideHint, CONFIG.hintDuration);
  }

  function hideHint() {
    if (!refs || refs.hint.hidden) return;
    clearTimeout(hintTimer);
    refs.hint.classList.remove('is-visible');
    try {
      sessionStorage.setItem('mlb-hint-seen', '1');
    } catch (e) {}
    setTimeout(function () {
      refs.hint.hidden = true;
    }, 320);
  }

  function lockScroll() {
    scrollY = window.scrollY || window.pageYOffset;

    // evita o "pulo" horizontal quando a barra de rolagem some no desktop
    const scrollbar = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbar > 0) document.body.style.paddingRight = scrollbar + 'px';

    document.body.classList.add('mlb-open');
    document.body.style.position = 'fixed';
    document.body.style.top = -scrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    document.body.classList.remove('mlb-open');
    document.body.style.paddingRight = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  /* --------------------------------------------------- zoom nativo do tema */

  /**
   * O zoom de hover do tema vive na classe `.zoomable`. Como o lightbox agora
   * cobre desktop e mobile, essa classe só atrapalha: tiramos ela e marcamos o
   * elemento com `.mlb-zoom-target`, que é o gancho do cursor "+" no CSS.
   *
   * Precisa rodar de novo a cada troca de variante, porque a Section Rendering
   * API substitui a galeria inteira por HTML novo, com a classe de volta.
   */
  function stripThemeZoom() {
    if (!CONFIG.disableThemeZoom) return;
    document.querySelectorAll('.product-gallery__media, .zoomable').forEach(
      function (node) {
        node.classList.remove('zoomable');
        node.classList.add('mlb-zoom-target');
      }
    );
  }

  let stripScheduled = false;
  function scheduleStrip() {
    if (stripScheduled) return;
    stripScheduled = true;
    requestAnimationFrame(function () {
      stripScheduled = false;
      stripThemeZoom();
    });
  }

  function watchGallery() {
    stripThemeZoom();
    // observa só inserção/remoção de nós: alterar classes não redispara,
    // então não há risco de loop
    new MutationObserver(scheduleStrip).observe(document.body, {
      childList: true,
      subtree: true
    });
    document.addEventListener('shopify:section:load', scheduleStrip);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchGallery);
  } else {
    watchGallery();
  }

  /* ------------------------------------------------------------ trigger -- */

  document.addEventListener(
    'click',
    function (event) {
      if (!isEnabled() || isOpen) return;

      const item = event.target.closest(CONFIG.itemSelector);
      if (!item) return;
      if (item.dataset.mediaType && item.dataset.mediaType !== 'image') return;

      const container =
        item.closest('product-gallery, .product-gallery, .product__media-list, [id^="MediaGallery"]') ||
        item.parentElement;
      if (!container) return;

      const list = collect(container, item);
      if (!list.length) return;

      // impede o zoom nativo do tema de abrir
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const start = Math.max(0, list.findIndex((entry) => entry.node === item));
      open(list, start);
    },
    true // fase de captura: roda antes dos handlers do tema
  );

  // API pública opcional (útil para debug / QA)
  window.MobileLightbox = { open: open, close: close, config: CONFIG };
})();